/**
 * Page section primitives:
 *   - PageHeader: title + actions row
 *   - SectionCard: rounded card surface
 *   - StatusPill: little colored chip
 *   - EmptyState: centered illustrated empty
 *   - Spinner / FullSpinner
 *   - QueryError: nicely-formatted React Query error
 *   - RangeTabs: range selector (Today/7d/30d/90d)
 *   - JsonViewer: pretty-printed JSON box
 *   - DataLoading skeleton lines
 */
import { classes } from '@/lib/utils';

export function PageHeader({ kicker = 'OneStamp', title, subtitle, actions, testID }) {
  return (
    <div className="flex items-end justify-between gap-6 mb-6" data-testid={testID}>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-widest text-gold/80 font-semibold">{kicker}</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1.5">{title}</h1>
        {subtitle ? <p className="text-white/55 text-sm mt-1.5 max-w-2xl">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2 shrink-0">{actions}</div> : null}
    </div>
  );
}

export function SectionCard({ children, className, padded = true, testID }) {
  return (
    <div
      data-testid={testID}
      className={classes(
        'bg-[#1A1A1A] border border-white/8 rounded-2xl',
        padded && 'p-6',
        className,
      )}
    >
      {children}
    </div>
  );
}

const PILL_VARIANT = {
  gold:    'bg-gold/15 text-gold border-gold/30',
  green:   'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  red:     'bg-red-500/15 text-red-300 border-red-500/30',
  amber:   'bg-amber-500/15 text-amber-300 border-amber-500/30',
  slate:   'bg-white/8 text-white/70 border-white/15',
  blue:    'bg-blue-500/15 text-blue-300 border-blue-500/30',
};

export function StatusPill({ children, variant = 'slate', className, testID }) {
  return (
    <span
      data-testid={testID}
      className={classes(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border whitespace-nowrap',
        PILL_VARIANT[variant] || PILL_VARIANT.slate,
        className,
      )}
    >
      {children}
    </span>
  );
}

export function EmptyState({ icon, title, subtitle, action, testID }) {
  return (
    <div
      data-testid={testID}
      className="border border-dashed border-white/10 rounded-2xl bg-[#1A1A1A]/60 p-12 text-center"
    >
      <div className="inline-block w-14 h-14 rounded-2xl border border-gold/30 bg-gold/10 grid place-items-center mb-4">
        {icon ? <span className="text-gold">{icon}</span> : <span className="text-gold font-bold">·</span>}
      </div>
      {title ? <div className="text-base font-semibold text-white">{title}</div> : null}
      {subtitle ? <p className="text-sm text-white/50 mt-1 max-w-md mx-auto">{subtitle}</p> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function Spinner({ className }) {
  return (
    <span
      className={classes(
        'inline-block w-5 h-5 border-2 border-gold border-t-transparent rounded-full animate-spin',
        className,
      )}
    />
  );
}

export function FullSpinner({ testID = 'page-loading' }) {
  return (
    <div className="py-24 flex items-center justify-center" data-testid={testID}>
      <Spinner className="w-8 h-8" />
    </div>
  );
}

export function QueryError({ error, onRetry, testID = 'query-error' }) {
  const message =
    error?.code === 'brand_context_mismatch'
      ? "You don't have access to this brand's data."
      : (error?.detail || error?.message || 'Something went wrong.');
  return (
    <div
      className="border border-red-500/20 bg-red-500/5 rounded-2xl p-6 flex items-start gap-4"
      data-testid={testID}
    >
      <div className="w-10 h-10 rounded-xl bg-red-500/15 border border-red-500/30 grid place-items-center text-red-300">!</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-red-200">Couldn't load this section</div>
        <div className="text-xs text-red-300/80 mt-1 break-words">{String(message)}</div>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 text-xs font-semibold text-gold hover:underline"
          >
            Try again
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function RangeTabs({ value, onChange, options, testID = 'range-tabs' }) {
  return (
    <div
      data-testid={testID}
      className="inline-flex p-1 bg-black/40 border border-white/8 rounded-xl"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          data-testid={`range-${o.value}`}
          className={classes(
            'px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors',
            value === o.value
              ? 'bg-gold/20 text-gold border border-gold/30'
              : 'text-white/60 hover:text-white border border-transparent',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function JsonViewer({ data, testID }) {
  let pretty;
  try { pretty = JSON.stringify(data, null, 2); } catch { pretty = String(data); }
  return (
    <pre
      data-testid={testID}
      className="text-[11px] leading-relaxed text-white/70 bg-black/40 border border-white/8 rounded-xl p-3 overflow-auto max-h-72 font-mono"
    >
      {pretty}
    </pre>
  );
}

export function SkeletonLines({ rows = 3 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-3 rounded bg-white/5 animate-pulse"
          style={{ width: `${60 + ((i * 13) % 35)}%` }}
        />
      ))}
    </div>
  );
}
